<template>
  <div ref="itemPanelRef">
    <CommonSliderDrawer :openDrawer="open" @closeModal="closePanel" zIndex="z-[9999999999999]">
      <div v-if="isProcessing" class="absolute inset-0 flex justify-center items-center bg-white/75 z-50">
        <NuxtImg class="w-20" src="/icons/loading.svg" />
      </div>

      <div class="absolute right-0 m-2 flex">
        <button
          @click="closePanel"
          type="button"
          class="z-50 text-gray-400 bg-light-silver rounded-full fill-secondary p-1"
        >
          <svg aria-hidden="true" class="w-5 h-5" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
          <span class="sr-only">Close modal</span>
        </button>
      </div>

      <div class="h-full bg-gray-50 flex flex-col shadow-[-3px_0px_6px_rgba(0,0,0,0.29)]">
        <!-- Header -->
        <div class="pl-[15px] py-3.5 border-b border-gray-200 flex justify-between items-center">
          <a
            @click="closePanel"
            class="text-secondary self-start my-4 cursor-pointer font-bold text-base flex items-center gap-2"
          >
            <IconsMoveLeftArrow />
            {{ $t("cart.title") }}
          </a>
        </div>

        <!-- Scrollable Content -->
        <div class="flex-grow overflow-y-auto px-6 pt-[43px] pb-6 space-y-4 bg-stone-white">
          <!-- Cart Title -->
          <div class="flex items-center">
            <IconsShoppingCart />
            <h3 class="ml-3 text-base font-bold text-color-black">
              {{ $t("cart.item_count") }}
              <span class="text-sm">({{ totalItems }})</span>
            </h3>
          </div>

          <!-- Cart Items -->
          <div
            v-if="totalItems > 0"
            class="border-t border-b divide-y divide-primary-light border-b-primary-light border-t-primary-light"
          >
            <CartProductWidget v-for="(item, index) in cartItems" :key="index" :item="item" />
          </div>

          <div v-else class="flex flex-col w-full border border-[#00A1AA99] rounded p-5 bg-white">
            <div class="flex items-center w-full py-1">
              <div class="flex space-x-2 items-baseline">
                <NuxtImg src="/icons/c-remove.svg" alt="c-remove.svg" class="w-4 my-2" />
                <span class="text-sm text-start font-semibold">
                  {{ $t("cart.empty") }}
                </span>
              </div>
            </div>
          </div>

          <!-- Reviews -->
          <div>
            <CommonCustomerReviewCount
              :prevent-navigation="true"
              @clicked="showReviewsModal = true"
              class="mt-[42px]"
              text-color-class="text-[#253754]"
            />
          </div>
        </div>

        <!-- Footer -->
        <div class="p-6 border-t border-[#00A1AA99] bg-white">
          <div class="flex justify-between items-center mb-4 text-color-black">
            <span class="text-lg font-bold">{{ $t("cart.subtotal") }}</span>
            <span class="text-lg font-bold">{{ $formatCurrency(totalPrice) }}</span>
          </div>

          <BaseButton
            :disabled="totalItems === 0"
            :text="$t('cart.proceed_to_checkout')"
            @buttonClicked="navigateTo(localeUrlQuery({ path: '/order-processing' }))"
            class="w-full h-12 text-lg"
          ></BaseButton>

          <div class="flex justify-center items-center space-x-2 mt-4">
            <IconsAcceptablePaymentMethods />
          </div>
        </div>
      </div>
    </CommonSliderDrawer>

    <ReviewsModal :show="showReviewsModal" @close="showReviewsModal = false" />
  </div>
</template>

<script setup lang="ts">
import { toRefs } from "vue";
import useCartSession from "~/composables/useCartSession";

const { computeCart, isProcessing } = useCartSession();

const itemPanelRef = ref<HTMLElement | null>(null);
const cartStore = useCartStore();
const { cartItems, totalItems, totalPrice } = toRefs(cartStore);
const showReviewsModal = ref(false);

const emit = defineEmits(["closeModal"]);

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
});

if (props.open === true) {
  computeCart();
}

watch(
  () => props.open,
  (newVal) => {
    if (newVal === true) {
      computeCart();
    }
  }
);

watch(cartStore.cartItems, () => computeCart(), { immediate: true });

const closePanel = () => {
  emit("closeModal");
};
</script>

<style scoped>
.text-color-black {
  color: #253754;
}
</style>
